import React, { useEffect, useState } from 'react';
import { i18n } from '../../i18n/i18n';
import { listNotificationControls, ParentControl, setNotificationControl } from '../../services/apiService';
import { showToast } from '../kid/Toast';

/**
 * Admin → Members: who decides each child's notifications (redesign step 6c).
 * "The child decides" (default), "Always on" (the child can't turn them off) or "Off".
 */
const OPTIONS: ParentControl[] = ['child', 'on', 'off'];

const ChildNotificationControls: React.FC<{ members: any[] }> = ({ members }) => {
  const kids = members.filter((m) => m.member_type === 'child' && !m.is_admin);
  const [controls, setControls] = useState<Record<string, ParentControl>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    listNotificationControls()
      .then((rows) => { if (alive) setControls(Object.fromEntries(rows.map((r) => [r.user_id, r.parent_control]))); })
      .catch(() => { /* shown as the default until it loads */ });
    return () => { alive = false; };
  }, []);

  if (kids.length === 0) return null;

  const choose = async (memberId: string, value: ParentControl) => {
    const before = controls[memberId] || 'child';
    if (value === before) return;
    setSaving(memberId);
    setControls((c) => ({ ...c, [memberId]: value }));
    try {
      await setNotificationControl(memberId, value);
      showToast({ message: i18n.t('parent_notifications.saved') });
    } catch (e: any) {
      setControls((c) => ({ ...c, [memberId]: before }));
      showToast({ message: e?.message || 'Error', tone: 'error' });
    } finally {
      setSaving(null);
    }
  };

  return (
    <section className="mt-8 text-kid-text dark:text-kid-text-dark">
      <h3 className="font-kid-heading font-extrabold text-2xl mb-1">🔔 {i18n.t('parent_notifications.title')}</h3>
      <p className="text-base mb-4">{i18n.t('parent_notifications.help')}</p>
      <ul className="space-y-3">
        {kids.map((m) => {
          const current = controls[m.id] || 'child';
          return (
            <li key={m.id} className="rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark p-4">
              <p className="font-bold text-lg mb-2">{m.name}</p>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={`${i18n.t('parent_notifications.title')}: ${m.name}`}>
                {OPTIONS.map((o) => (
                  <button
                    key={o}
                    type="button"
                    role="radio"
                    aria-checked={current === o}
                    disabled={saving === m.id}
                    onClick={() => choose(m.id, o)}
                    className={`min-h-[44px] px-4 rounded-full border-2 font-bold ${
                      current === o
                        ? 'bg-kid-green-bg dark:bg-green-900/40 border-kid-green text-kid-green-shadow dark:text-green-200'
                        : 'bg-kid-card dark:bg-kid-card-dark border-kid-border-strong dark:border-kid-border-strong-dark'
                    }`}
                  >
                    {i18n.t(`parent_notifications.${o}`)}
                  </button>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default ChildNotificationControls;
